// Production-Ready Podcast Player
// Features: Audio visualization, playback speed, chapter markers, transcript search, download

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  Volume1,
  Video, 
  Download,
  Share2,
  Settings,
  Repeat,
  Clock,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  FileImage,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { PodcastResult, PodcastSegment, PodcastConfig } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';
import { VideoGeneratorButton } from './VideoGeneratorButton';

interface PodcastPlayerProps {
  podcast: PodcastResult;
  podcastConfig?: PodcastConfig;
  className?: string;
}

// Playback speed options
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

// VideoDisplay removed — HeyGen replaced by Remotion in VideoGeneratorButton

// Enhanced avatar display with animated waveform
function AvatarDisplay({
  avatarId,
  avatarName,
  isSpeaking,
  hostIndex,
  isActive,
}: {
  avatarId: string;
  avatarName: string;
  isSpeaking: boolean;
  hostIndex: number;
  isActive: boolean;
}) {
  const colorSchemes = [
    { bg: 'bg-blue-50 dark:bg-blue-950/40', border: 'border-blue-400', ring: 'ring-blue-500', avatar: 'bg-blue-200 text-blue-700 dark:bg-blue-800 dark:text-blue-200' },
    { bg: 'bg-emerald-50 dark:bg-emerald-950/40', border: 'border-emerald-400', ring: 'ring-emerald-500', avatar: 'bg-emerald-200 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200' },
  ];
  const colors = colorSchemes[hostIndex % colorSchemes.length];

  return (
    <div
      className={cn(
        'relative flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-300',
        colors.bg,
        colors.border,
        isSpeaking && `ring-4 ${colors.ring} ring-opacity-50 scale-105`,
        !isActive && 'opacity-60'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold transition-all',
          colors.avatar,
          isSpeaking && 'animate-pulse'
        )}
      >
        {avatarName.charAt(0).toUpperCase()}
      </div>
      <span className="mt-2 text-sm font-medium">{avatarName}</span>
      
      {/* Speaking indicator waveform */}
      {isSpeaking && (
        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex items-end gap-0.5 h-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-1 bg-current rounded-full animate-pulse"
              style={{
                height: `${Math.random() * 100}%`,
                animationDelay: `${i * 100}ms`,
                animationDuration: '300ms',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Audio visualization bars
function AudioVisualizer({ isPlaying }: { isPlaying: boolean }) {
  return (
    <div className="flex items-end gap-0.5 h-8">
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'w-1 bg-primary/60 rounded-full transition-all',
            isPlaying ? 'animate-pulse' : ''
          )}
          style={{
            height: isPlaying ? `${20 + Math.random() * 80}%` : '20%',
            animationDelay: `${i * 50}ms`,
            animationDuration: '400ms',
          }}
        />
      ))}
    </div>
  );
}

export function PodcastPlayer({ podcast, podcastConfig, className }: PodcastPlayerProps) {
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [progress, setProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isLooping, setIsLooping] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTranscript, setShowTranscript] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [useCombinedAudio, setUseCombinedAudio] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Export functionality
  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();

  const { script, audioSegments, isVideoPodcast, videoPodcast, combinedAudioUrl } = podcast;
  const currentSegment = script.segments[currentSegmentIndex];
  const currentAudioSegment = audioSegments?.find(s => s.segmentIndex === currentSegmentIndex);
  const currentHostIndex = currentSegment?.hostIndex ?? (currentSegment?.speaker === 'host1' ? 0 : 1);

  // Calculate segment timestamps for combined audio (estimate ~12s per segment average)
  const segmentTimestamps = useMemo(() => {
    const timestamps: { start: number; end: number }[] = [];
    let cumulativeTime = 0;
    const avgSegmentDuration = 12; // seconds per segment estimate
    
    script.segments.forEach((seg, idx) => {
      const start = cumulativeTime;
      const end = start + avgSegmentDuration;
      timestamps.push({ start, end });
      cumulativeTime = end;
    });
    
    return timestamps;
  }, [script.segments]);

  // Search through transcript
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    return script.segments
      .map((seg, idx) => ({ ...seg, index: idx }))
      .filter(seg => seg.text.toLowerCase().includes(term));
  }, [searchTerm, script.segments]);

  // Load audio - prefer combined audio for seamless playback
  useEffect(() => {
    if (!audioRef.current) return;
    
    if (combinedAudioUrl && useCombinedAudio) {
      // Use combined audio for seamless playback
      if (audioRef.current.src !== combinedAudioUrl) {
        audioRef.current.src = combinedAudioUrl;
      }
      audioRef.current.playbackRate = playbackSpeed;
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    } else if (currentAudioSegment?.audioUrl) {
      // Fallback to individual segments
      audioRef.current.src = currentAudioSegment.audioUrl;
      audioRef.current.playbackRate = playbackSpeed;
      audioRef.current.volume = isMuted ? 0 : volume / 100;
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      }
    }
  }, [combinedAudioUrl, useCombinedAudio, currentSegmentIndex, currentAudioSegment?.audioUrl]);

  // Update playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  // Handle audio end
  const handleAudioEnd = () => {
    if (isLooping) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(console.error);
      }
    } else if (!combinedAudioUrl || !useCombinedAudio) {
      // Only auto-advance for individual segments
      if (currentSegmentIndex < script.segments.length - 1) {
        setCurrentSegmentIndex(prev => prev + 1);
      } else {
        setIsPlaying(false);
        setCurrentSegmentIndex(0);
        toast.success('Podcast finished!');
      }
    } else {
      // Combined audio finished
      setIsPlaying(false);
      setCurrentSegmentIndex(0);
      toast.success('Podcast finished!');
    }
  };

  // Time update handler - also updates current segment based on time
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(isNaN(prog) ? 0 : prog);
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
      
      // Update current segment based on playback time (for combined audio)
      if (combinedAudioUrl && useCombinedAudio && audioRef.current.duration) {
        const segmentDuration = audioRef.current.duration / script.segments.length;
        const newSegmentIndex = Math.min(
          Math.floor(audioRef.current.currentTime / segmentDuration),
          script.segments.length - 1
        );
        if (newSegmentIndex !== currentSegmentIndex && newSegmentIndex >= 0) {
          setCurrentSegmentIndex(newSegmentIndex);
        }
      }
    }
  };

  // Skip backward/forward 10 seconds
  const skip = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(
        audioRef.current.currentTime + seconds,
        audioRef.current.duration || 0
      ));
    }
  };

  // Play/Pause toggle
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  // Skip controls - for combined audio, jump to segment time
  const skipPrevious = () => {
    if (currentSegmentIndex > 0) {
      const newIndex = currentSegmentIndex - 1;
      setCurrentSegmentIndex(newIndex);
      
      // For combined audio, seek to segment start
      if (combinedAudioUrl && useCombinedAudio && audioRef.current && audioRef.current.duration) {
        const segmentDuration = audioRef.current.duration / script.segments.length;
        audioRef.current.currentTime = newIndex * segmentDuration;
      }
    }
  };

  const skipNext = () => {
    if (currentSegmentIndex < script.segments.length - 1) {
      const newIndex = currentSegmentIndex + 1;
      setCurrentSegmentIndex(newIndex);
      
      // For combined audio, seek to segment start
      if (combinedAudioUrl && useCombinedAudio && audioRef.current && audioRef.current.duration) {
        const segmentDuration = audioRef.current.duration / script.segments.length;
        audioRef.current.currentTime = newIndex * segmentDuration;
      }
    }
  };

  // Format time
  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Download audio
  const downloadAudio = async () => {
    const audioUrl = combinedAudioUrl || currentAudioSegment?.audioUrl;
    if (!audioUrl) {
      toast.error('No audio available to download');
      return;
    }

    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${script.title.replace(/\s+/g, '-').toLowerCase()}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started!');
    } catch (error) {
      toast.error('Failed to download audio');
    }
  };

  // Share podcast
  const sharePodcast = async () => {
    try {
      await navigator.share({
        title: script.title,
        text: script.description || `Listen to ${script.title}`,
      });
    } catch {
      // Fallback: copy title to clipboard
      await navigator.clipboard.writeText(script.title);
      toast.success('Title copied to clipboard');
    }
  };

  // Export transcript
  const exportTranscript = (format: 'pdf' | 'docx') => {
    if (format === 'pdf') {
      exportToPdf('podcast-transcript', podcast);
    } else {
      exportToDocx('podcast-transcript', podcast);
    }
  };

  // Volume icon based on level
  const VolumeIcon = isMuted ? VolumeX : volume < 50 ? Volume1 : Volume2;

  return (
    <TooltipProvider>
      <div className={cn('flex flex-col', className)}>
        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          onEnded={handleAudioEnd}
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedMetadata={() => {
            if (audioRef.current) {
              const audioDuration = audioRef.current.duration;
              // Handle NaN or Infinity duration
              if (audioDuration && isFinite(audioDuration) && audioDuration > 0) {
                setDuration(audioDuration);
              } else {
                // Fallback: estimate duration based on segment count (~12 seconds per segment)
                const estimatedDuration = script.segments.length * 12;
                setDuration(estimatedDuration);
              }
            }
          }}
          onDurationChange={() => {
            // Also handle duration changes that happen after initial load
            if (audioRef.current) {
              const audioDuration = audioRef.current.duration;
              if (audioDuration && isFinite(audioDuration) && audioDuration > 0) {
                setDuration(audioDuration);
              }
            }
          }}
        />

        {/* Video podcast display - Animated avatar display */}
        {isVideoPodcast && (
          <Card className="mb-4 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-center justify-center gap-8 p-6 bg-gradient-to-b from-muted/50 to-background">
                <AvatarDisplay
                  avatarId="host1"
                  avatarName={script.segments[0]?.speakerName || 'Host 1'}
                  isSpeaking={isPlaying && currentHostIndex === 0}
                  hostIndex={0}
                  isActive={currentHostIndex === 0}
                />
                <AvatarDisplay
                  avatarId="host2"
                  avatarName={script.segments.find(s => s.speaker === 'host2')?.speakerName || 'Host 2'}
                  isSpeaking={isPlaying && currentHostIndex === 1}
                  hostIndex={1}
                  isActive={currentHostIndex === 1}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current segment text */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge variant={currentHostIndex === 0 ? 'default' : 'secondary'}>
                  {currentSegment?.speakerName}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Segment {currentSegmentIndex + 1} of {script.segments.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isVideoPodcast && (
                  <Badge variant="outline" className="gap-1">
                    <Video className="h-3 w-3" />
                    Video
                  </Badge>
                )}
                <Badge variant="outline" className="gap-1">
                  <Clock className="h-3 w-3" />
                  {script.totalDuration}
                </Badge>
                {/* Generate Video Button — auto-triggers in background after audio is ready */}
                <VideoGeneratorButton podcast={podcast} podcastConfig={podcastConfig} title={script.title} autoTrigger />
              </div>
            </div>
            <p className="text-sm leading-relaxed">{currentSegment?.text}</p>
          </CardContent>
        </Card>

        {/* Audio Visualizer */}
        <div className="flex justify-center mb-4">
          <AudioVisualizer isPlaying={isPlaying} />
        </div>

        {/* Progress bar with time */}
        <div className="mb-4">
          <Slider
            value={[progress]}
            max={100}
            step={0.1}
            onValueChange={(value) => {
              if (audioRef.current && audioRef.current.duration) {
                audioRef.current.currentTime = (value[0] / 100) * audioRef.current.duration;
                setProgress(value[0]);
              }
            }}
            className="cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Main Controls */}
        <div className="flex items-center justify-between mb-4">
          {/* Left: Volume */}
          <div className="flex items-center gap-2 flex-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsMuted(!isMuted)}
                >
                  <VolumeIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isMuted ? 'Unmute' : 'Mute'}</TooltipContent>
            </Tooltip>
            <Slider
              value={[isMuted ? 0 : volume]}
              max={100}
              step={1}
              onValueChange={(value) => {
                setVolume(value[0]);
                setIsMuted(value[0] === 0);
              }}
              className="w-20"
            />
          </div>

          {/* Center: Playback controls */}
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={() => skip(-10)}>
                  <SkipBack className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Back 10s</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={skipPrevious} disabled={currentSegmentIndex === 0}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Previous segment</TooltipContent>
            </Tooltip>

            <Button
              size="icon"
              className="h-14 w-14 rounded-full"
              onClick={togglePlay}
              disabled={!currentAudioSegment?.audioUrl}
            >
              {isPlaying ? (
                <Pause className="h-6 w-6" />
              ) : (
                <Play className="h-6 w-6 ml-0.5" />
              )}
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={skipNext} 
                  disabled={currentSegmentIndex === script.segments.length - 1}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Next segment</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={() => skip(10)}>
                  <SkipForward className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Forward 10s</TooltipContent>
            </Tooltip>
          </div>

          {/* Right: Settings & Actions */}
          <div className="flex items-center gap-1 flex-1 justify-end">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={isLooping ? 'default' : 'ghost'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsLooping(!isLooping)}
                >
                  <Repeat className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isLooping ? 'Loop on' : 'Loop off'}</TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs">
                  {playbackSpeed}x
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Playback Speed</DropdownMenuLabel>
                {PLAYBACK_SPEEDS.map(speed => (
                  <DropdownMenuItem
                    key={speed}
                    onClick={() => setPlaybackSpeed(speed)}
                    className={playbackSpeed === speed ? 'bg-muted' : ''}
                  >
                    {speed}x
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={downloadAudio}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Audio
                </DropdownMenuItem>
                <DropdownMenuItem onClick={sharePodcast}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowTranscript(!showTranscript)}>
                  {showTranscript ? 'Hide' : 'Show'} Transcript
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Transcript with search */}
        {showTranscript && (
          <Card>
            <CardContent className="p-3">
              {/* Transcript search */}
              <div className="relative mb-3">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search transcript..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-8"
                />
              </div>

              {/* Search results or full transcript */}
              <ScrollArea className="h-[200px]">
                <div className="space-y-1">
                  {(searchTerm ? searchResults : script.segments.map((s, i) => ({ ...s, index: i }))).map((segment) => {
                    const hostIdx = segment.hostIndex ?? (segment.speaker === 'host1' ? 0 : 1);
                    const isActive = segment.index === currentSegmentIndex;
                    
                    return (
                      <button
                        key={segment.index}
                        onClick={() => setCurrentSegmentIndex(segment.index)}
                        className={cn(
                          'w-full text-left p-2 rounded-md text-sm transition-colors',
                          'hover:bg-muted/50',
                          isActive && 'bg-primary/10 ring-1 ring-primary',
                          hostIdx === 0 ? 'border-l-2 border-blue-400' : 'border-l-2 border-emerald-400'
                        )}
                      >
                        <span className="font-medium text-xs text-muted-foreground">
                          {segment.speakerName}:
                        </span>{' '}
                        <span className={cn(
                          'line-clamp-2',
                          searchTerm && segment.text.toLowerCase().includes(searchTerm.toLowerCase()) && 
                          'bg-yellow-100 dark:bg-yellow-900/30'
                        )}>
                          {segment.text}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}

export default PodcastPlayer;
